/**
 * HeatThermometer — Visual heat gauge replacing numeric TAG scores.
 *
 * Customers don't understand "Score 73, Grade B."
 * They understand: this account is hot. Act now.
 *
 * Maps TAG score (0-100) to a visual temperature:
 *   0-19:  Cold (gray)
 *   20-39: Aware (blue)
 *   40-59: Evaluating (yellow)
 *   60-79: Interested (orange)
 *   80+:   Ready to buy (red)
 *
 * Surging accounts get a pulse animation.
 */

import React from 'react'

interface HeatThermometerProps {
  score: number
  trend?: string  // 'surging' | 'rising' | 'stable' | 'declining' | 'cold'
  size?: 'sm' | 'md' | 'lg'
  showLabel?: boolean
}

interface HeatLevel {
  label: string
  color: string
  bgColor: string
  fill: number  // 0-100 percentage
}

const getHeatLevel = (score: number): HeatLevel => {
  if (score >= 80) return { label: 'Ready to buy', color: '#ef4444', bgColor: 'rgba(239,68,68,0.15)', fill: 100 }
  if (score >= 60) return { label: 'Interested', color: '#f97316', bgColor: 'rgba(249,115,22,0.15)', fill: 75 }
  if (score >= 40) return { label: 'Evaluating', color: '#eab308', bgColor: 'rgba(234,179,8,0.15)', fill: 50 }
  if (score >= 20) return { label: 'Aware', color: '#3b82f6', bgColor: 'rgba(59,130,246,0.15)', fill: 25 }
  return { label: 'Cold', color: 'var(--text-muted)', bgColor: 'rgba(148,163,184,0.1)', fill: 5 }
}

const trendArrows: Record<string, string> = {
  surging: '⬆⬆',
  rising: '↑',
  stable: '→',
  declining: '↓',
  cold: '',
}

const sizes = {
  sm: { width: 8, height: 32, bulb: 12, fontSize: 9 },
  md: { width: 10, height: 44, bulb: 16, fontSize: 10 },
  lg: { width: 14, height: 56, bulb: 22, fontSize: 12 },
}

export const HeatThermometer: React.FC<HeatThermometerProps> = ({
  score,
  trend = 'stable',
  size = 'md',
  showLabel = true,
}) => {
  const heat = getHeatLevel(score)
  const isSurging = trend === 'surging'
  const dim = sizes[size]
  const arrow = trendArrows[trend] || ''

  return (
    <div className="flex items-center gap-2" title={`${heat.label} (score: ${score})`}>
      {/* Thermometer SVG */}
      <div
        className={`relative flex flex-col items-center ${isSurging ? 'animate-pulse' : ''}`}
        style={{ width: dim.bulb + 4, height: dim.height + dim.bulb / 2 }}
      >
        {/* Tube */}
        <div
          className="rounded-t-full overflow-hidden relative"
          style={{
            width: dim.width,
            height: dim.height,
            background: 'var(--border)',
          }}
        >
          {/* Fill from bottom */}
          <div
            className="absolute bottom-0 left-0 right-0 rounded-t-full transition-all duration-700"
            style={{
              height: `${heat.fill}%`,
              background: `linear-gradient(to top, ${heat.color}, ${heat.color}cc)`,
            }}
          />
        </div>
        {/* Bulb */}
        <div
          className="rounded-full border-2 -mt-1"
          style={{
            width: dim.bulb,
            height: dim.bulb,
            backgroundColor: heat.color,
            borderColor: heat.color,
            boxShadow: isSurging ? `0 0 8px ${heat.color}80` : 'none',
          }}
        />
      </div>

      {/* Label + trend */}
      {showLabel && (
        <div className="flex flex-col">
          <span
            className="font-medium leading-tight"
            style={{ color: heat.color, fontSize: dim.fontSize }}
          >
            {heat.label}
          </span>
          {arrow && (
            <span
              className="leading-tight"
              style={{
                color: isSurging ? heat.color : 'var(--text-muted)',
                fontSize: dim.fontSize - 1,
              }}
            >
              {arrow} {trend}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/**
 * Compact inline version for table rows and cards.
 * Shows just the thermometer bar (horizontal) with color.
 */
export const HeatBar: React.FC<{
  score: number
  trend?: string
  width?: number
}> = ({ score, trend, width = 48 }) => {
  const heat = getHeatLevel(score)
  const isSurging = trend === 'surging'

  return (
    <div
      className={`flex items-center gap-1.5 ${isSurging ? 'animate-pulse' : ''}`}
      title={`${heat.label} (score: ${score})`}
    >
      <div
        className="h-2 rounded-full overflow-hidden"
        style={{ width, background: 'var(--border)' }}
      >
        <div
          className="h-full rounded-full transition-all duration-700"
          style={{
            width: `${heat.fill}%`,
            background: heat.color,
          }}
        />
      </div>
      <span className="text-xs font-medium" style={{ color: heat.color }}>
        {heat.label.split(' ')[0]}
      </span>
    </div>
  )
}
